import { supabase } from './supabase';
import { logger } from './logger';
import {
  CANDIDATE_STEPS,
  EMPLOYER_STEPS,
  stepBand,
  type BreadcrumbRole,
  type BreadcrumbStep,
  type StairBand,
} from './breadcrumbsCatalog';

export interface FunnelRow {
  id: string;
  n: number;
  phase: string;
  label: string;
  fork?: boolean;
  band: StairBand;
  /** Effectif affiché (intersecté avec les marches d’avant si `stair` / `pre`). */
  reached: number;
  /** Effectif brut, sans imposer les marches précédentes. */
  reachedAny: number;
  pctOfCohort: number;
  pctOfPrev: number | null;
  dropOff: number | null;
  /** Largeur de barre 0–100, relative à la tête de chaîne (escalier) ou à la cohorte (jalons). */
  barPct: number;
  medianHoursToNext: number | null;
}

export interface FunnelResult {
  role: BreadcrumbRole;
  cohort: number;
  rows: FunnelRow[];
}

type TouchMap = Map<string, Map<string, number>>;

function daysAgo(n: number): string | null {
  if (n <= 0) return null;
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
}

function addTouch(map: TouchMap, actor: string | null | undefined, stepId: string, at: string | null | undefined) {
  if (!actor || !at) return;
  const ts = new Date(at).getTime();
  if (!Number.isFinite(ts)) return;
  let inner = map.get(actor);
  if (!inner) {
    inner = new Map();
    map.set(actor, inner);
  }
  const prev = inner.get(stepId);
  if (prev == null || ts < prev) inner.set(stepId, ts);
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  const v = s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
  return Math.round(v * 10) / 10;
}

function propsOf(row: { props?: Record<string, unknown> | string }): Record<string, unknown> {
  const p = row.props;
  if (!p) return {};
  if (typeof p === 'string') {
    try { return JSON.parse(p); } catch { return {}; }
  }
  return p;
}

async function fetchAll<T>(table: string, columns: string, apply: (q: any) => any): Promise<T[]> {
  const page = 1000;
  let from = 0;
  const out: T[] = [];
  for (;;) {
    let q = supabase.from(table).select(columns).range(from, from + page - 1);
    q = apply(q);
    const { data, error } = await q;
    if (error) throw error;
    const rows = (data || []) as T[];
    out.push(...rows);
    if (rows.length < page) break;
    from += page;
    if (from > 20000) break;
  }
  return out;
}

export async function fetchBreadcrumbFunnel(params: {
  role: BreadcrumbRole;
  period: number;
  excludeSeeds: boolean;
}): Promise<FunnelResult> {
  const since = daysAgo(params.period);
  const steps: BreadcrumbStep[] = params.role === 'candidate' ? CANDIDATE_STEPS : EMPLOYER_STEPS;
  const touches: TouchMap = new Map();

  try {
    let profileQuery = supabase
      .from('profiles')
      .select('id, role, seed_tag, created_at');
    if (params.role) profileQuery = profileQuery.eq('role', params.role);
    if (since) profileQuery = profileQuery.gte('created_at', since);
    const { data: profiles, error: pErr } = await profileQuery;
    if (pErr) throw pErr;

    const cohortProfiles = (profiles || []).filter((p: any) => {
      if (params.excludeSeeds && p.seed_tag) return false;
      return p.role === params.role;
    });
    const cohortIds = new Set(cohortProfiles.map((p: any) => p.id as string));
    const cohort = cohortIds.size;

    for (const p of cohortProfiles) {
      addTouch(touches, p.id, params.role === 'candidate' ? 'c.signup' : 'e.signup', p.created_at);
    }

    const { data: events, error: eErr } = await supabase
      .from('user_events')
      .select('user_id, anonymous_id, event, props, created_at')
      .limit(20000);
    if (eErr) throw eErr;

    const welcomeRole = new Map<string, string>();
    for (const ev of events || []) {
      if (ev.event !== 'welcome_role') continue;
      const role = String(propsOf(ev).role || '');
      const actor = ev.user_id || ev.anonymous_id;
      if (actor && role) welcomeRole.set(actor, role);
    }

    const actorRole = (ev: any): BreadcrumbRole | 'both' | null => {
      if (ev.user_id && cohortIds.has(ev.user_id)) return params.role;
      if (ev.user_id) return null;
      const guessed = welcomeRole.get(ev.anonymous_id) || welcomeRole.get(ev.user_id);
      if (guessed === params.role) return params.role;
      if (!guessed && (ev.event === 'lang_chosen' || ev.event === 'carousel_done')) return 'both';
      return null;
    };

    for (const ev of events || []) {
      const ar = actorRole(ev);
      if (!ar) continue;
      const actor = ev.user_id || ev.anonymous_id;
      const p = propsOf(ev);
      const at = ev.created_at;
      const prefix = params.role === 'candidate' ? 'c' : 'e';

      if (ev.event === 'lang_chosen') addTouch(touches, actor, `${prefix}.lang`, at);
      if (ev.event === 'carousel_done') addTouch(touches, actor, `${prefix}.carousel`, at);
      if (ev.event === 'welcome_role' && p.role === params.role) {
        addTouch(touches, actor, `${prefix}.welcome`, at);
      }
      if (params.role === 'candidate') {
        if (ev.event === 'register_phone') addTouch(touches, actor, 'c.phone', at);
        if (ev.event === 'otp_verified') addTouch(touches, actor, 'c.otp', at);
        if (ev.event === 'onboarding_skipped') addTouch(touches, actor, 'c.onb_skip', at);
        if (ev.event === 'step_reached' && p.wizard === 'onboarding') {
          const n = Number(p.step);
          if (n >= 1 && n <= 8) addTouch(touches, actor, `c.onb_${n}`, at);
        }
        if (ev.event === 'screen_view') {
          const route = String(p.raw || p.route || '');
          if (route.includes('(candidate)/home') || route === 'candidate/home') addTouch(touches, actor, 'c.feed_load', at);
          if (route.includes('offer-detail') && route.includes('candidate')) addTouch(touches, actor, 'c.open_offer', at);
          if (route.includes('employer-detail')) addTouch(touches, actor, 'c.open_employer', at);
          if (route.includes('candidate-detail') && route.includes('candidate')) addTouch(touches, actor, 'c.open_candidate', at);
          if (route.includes('(candidate)/offers') || route === 'candidate/offers') addTouch(touches, actor, 'c.offers_tab', at);
          if (route.includes('(candidate)/messages')) addTouch(touches, actor, 'c.inbox', at);
        }
        if (ev.event === 'feed_mode') {
          if (p.to === 'foryou' || p.mode === 'foryou') addTouch(touches, actor, 'c.mode_foryou', at);
          if (p.to === 'trending' || p.mode === 'trending') addTouch(touches, actor, 'c.mode_trending', at);
        }
        if (ev.event === 'feed_filters') addTouch(touches, actor, 'c.filters', at);
        if (ev.event === 'search_query') addTouch(touches, actor, 'c.search', at);
        if (ev.event === 'search_result_open') addTouch(touches, actor, 'c.search_result', at);
      } else {
        if (ev.event === 'register_step') {
          if (Number(p.step) === 1) addTouch(touches, actor, 'e.reg_you', at);
          if (Number(p.step) === 2) addTouch(touches, actor, 'e.reg_siret', at);
        }
        if (ev.event === 'step_reached' && p.wizard === 'create_offer') {
          const step = String(p.step);
          const map: Record<string, string> = {
            sector: 'e.offer_sector',
            place: 'e.offer_place',
            contract: 'e.offer_contract',
            requirements: 'e.offer_requirements',
            advantages: 'e.offer_advantages',
            visual: 'e.offer_visual',
          };
          if (map[step]) addTouch(touches, actor, map[step], at);
        }
        if (ev.event === 'screen_view') {
          const route = String(p.raw || p.route || '');
          if (route.includes('(employer)/home') || route === 'employer/home') addTouch(touches, actor, 'e.feed_load', at);
          if (route.includes('(employer)/offers') && !route.includes('offer-applications') && !route.includes('offer-detail')) addTouch(touches, actor, 'e.offers_list', at);
          if (route.includes('offer-applications')) addTouch(touches, actor, 'e.applications_list', at);
          if (route.includes('viewed-profiles')) addTouch(touches, actor, 'e.viewed_list', at);
          if (route.includes('(employer)/messages')) addTouch(touches, actor, 'e.inbox', at);
          if (route.includes('(employer)/dashboard')) addTouch(touches, actor, 'e.dashboard', at);
        }
        if (ev.event === 'feed_mode') {
          if (p.to === 'foryou' || p.mode === 'foryou') addTouch(touches, actor, 'e.mode_foryou', at);
          if (p.to === 'trending' || p.mode === 'trending') addTouch(touches, actor, 'e.mode_trending', at);
        }
        if (ev.event === 'feed_filters') addTouch(touches, actor, 'e.filters', at);
        if (ev.event === 'search_query') {
          if (p.kind === 'matching') addTouch(touches, actor, 'e.search_matching', at);
          else addTouch(touches, actor, 'e.search_global', at);
        }
      }
    }

    if (params.role === 'candidate') {
      const cps = await fetchAll<any>('candidate_profiles', 'user_id, profile_complete, onboarding_step, onboarding_skipped_at', (q) => q);
      for (const row of cps) {
        if (!cohortIds.has(row.user_id)) continue;
        if (row.onboarding_skipped_at) addTouch(touches, row.user_id, 'c.onb_skip', row.onboarding_skipped_at);
        if (row.profile_complete) {
          const t = cohortProfiles.find((p: any) => p.id === row.user_id)?.created_at;
          for (let i = 1; i <= 8; i++) addTouch(touches, row.user_id, `c.onb_${i}`, t);
        }
        const maxStep = Number(row.onboarding_step) || 0;
        for (let i = 1; i <= Math.min(maxStep + 1, 8); i++) {
          if (!touches.get(row.user_id)?.has(`c.onb_${i}`)) {
            addTouch(touches, row.user_id, `c.onb_${i}`, cohortProfiles.find((p: any) => p.id === row.user_id)?.created_at);
          }
        }
      }

      const vis = await fetchAll<any>('video_interactions', 'user_id, interaction, created_at', (q) => q);
      for (const row of vis) {
        if (!cohortIds.has(row.user_id)) continue;
        if (row.interaction === 'view') addTouch(touches, row.user_id, 'c.video_view', row.created_at);
        else if (row.interaction === 'like') addTouch(touches, row.user_id, 'c.like', row.created_at);
        else if (row.interaction === 'share') addTouch(touches, row.user_id, 'c.share', row.created_at);
        else if (String(row.interaction).startsWith('react_')) addTouch(touches, row.user_id, 'c.react', row.created_at);
      }

      const follows = await fetchAll<any>('follows', 'follower_id, created_at', (q) => q);
      for (const row of follows) {
        if (cohortIds.has(row.follower_id)) addTouch(touches, row.follower_id, 'c.follow', row.created_at);
      }

      const favs = await fetchAll<any>('favorites', 'user_id, target_type, created_at', (q) => q.eq('target_type', 'offer'));
      for (const row of favs) {
        if (cohortIds.has(row.user_id)) addTouch(touches, row.user_id, 'c.favorite_offer', row.created_at);
      }

      const apps = await fetchAll<any>('applications', 'candidate_id, source, applied_at', (q) => q);
      const { data: cpMapRows } = await supabase.from('candidate_profiles').select('id, user_id');
      const cpToUser = new Map((cpMapRows || []).map((r: any) => [r.id, r.user_id]));
      for (const row of apps) {
        const uid = cpToUser.get(row.candidate_id);
        if (!uid || !cohortIds.has(uid)) continue;
        if (row.source === 'feed') addTouch(touches, uid, 'c.apply_feed', row.applied_at);
        else addTouch(touches, uid, 'c.apply_detail', row.applied_at);
      }

      const msgs = await fetchAll<any>('messages', 'sender_id, created_at', (q) => q);
      for (const row of msgs) {
        if (cohortIds.has(row.sender_id)) addTouch(touches, row.sender_id, 'c.message', row.created_at);
      }
    } else {
      const eps = await fetchAll<any>('employer_profiles', 'id, user_id, email_verified_at, email_code_sent_at', (q) => q);
      const epToUser = new Map<string, string>();
      for (const row of eps) {
        epToUser.set(row.id, row.user_id);
        if (!cohortIds.has(row.user_id)) continue;
        if (row.email_code_sent_at) addTouch(touches, row.user_id, 'e.email_sent', row.email_code_sent_at);
        if (row.email_verified_at) addTouch(touches, row.user_id, 'e.email_verified', row.email_verified_at);
      }

      const vis = await fetchAll<any>('video_interactions', 'user_id, interaction, created_at', (q) => q);
      for (const row of vis) {
        if (!cohortIds.has(row.user_id)) continue;
        if (row.interaction === 'view') addTouch(touches, row.user_id, 'e.video_view', row.created_at);
        else if (row.interaction === 'like') addTouch(touches, row.user_id, 'e.like', row.created_at);
        else if (row.interaction === 'share') addTouch(touches, row.user_id, 'e.share', row.created_at);
        else if (String(row.interaction).startsWith('react_')) addTouch(touches, row.user_id, 'e.react', row.created_at);
      }

      const follows = await fetchAll<any>('follows', 'follower_id, created_at', (q) => q);
      for (const row of follows) {
        if (cohortIds.has(row.follower_id)) addTouch(touches, row.follower_id, 'e.follow', row.created_at);
      }

      const favs = await fetchAll<any>('favorites', 'user_id, target_type, created_at', (q) => q.eq('target_type', 'candidate'));
      for (const row of favs) {
        if (cohortIds.has(row.user_id)) addTouch(touches, row.user_id, 'e.favorite_candidate', row.created_at);
      }

      const offers = await fetchAll<any>('job_offers', 'employer_id, created_at', (q) => q);
      for (const row of offers) {
        const uid = epToUser.get(row.employer_id);
        if (uid && cohortIds.has(uid)) addTouch(touches, uid, 'e.offer_published', row.created_at);
      }

      const videos = await fetchAll<any>('employer_videos', 'employer_id, offer_id, created_at', (q) => q);
      for (const row of videos) {
        if (row.offer_id) continue;
        const uid = epToUser.get(row.employer_id);
        if (uid && cohortIds.has(uid)) addTouch(touches, uid, 'e.free_content', row.created_at);
      }

      const views = await fetchAll<any>('profile_views', 'employer_id, viewed_at', (q) => q);
      for (const row of views) {
        const uid = epToUser.get(row.employer_id);
        if (uid && cohortIds.has(uid)) addTouch(touches, uid, 'e.profile_view', row.viewed_at);
      }

      const sels = await fetchAll<any>('employer_selections', 'employer_id, selected_at', (q) => q);
      for (const row of sels) {
        const uid = epToUser.get(row.employer_id);
        if (uid && cohortIds.has(uid)) addTouch(touches, uid, 'e.contact', row.selected_at);
      }

      const msgs = await fetchAll<any>('messages', 'sender_id, created_at', (q) => q);
      for (const row of msgs) {
        if (cohortIds.has(row.sender_id)) addTouch(touches, row.sender_id, 'e.message', row.created_at);
      }
    }

    const actorsWith = (stepId: string): Set<string> => {
      const s = new Set<string>();
      for (const [actor, inner] of touches) {
        if (inner.has(stepId)) s.add(actor);
      }
      return s;
    };
    const intersect = (a: Set<string>, b: Set<string>): Set<string> => {
      const out = new Set<string>();
      for (const x of a) if (b.has(x)) out.add(x);
      return out;
    };

    const linear = steps.filter((s) => !s.fork);
    const nestedActors: Set<string>[] = [];
    const chainHeadReached: number[] = [];
    let remaining: Set<string> | null = null;
    let prevBand: StairBand | null = null;
    let headSize = 0;

    for (const step of steps) {
      const band = stepBand(step);
      const raw = actorsWith(step.id);
      const chained = band === 'pre' || band === 'stair';
      if (!chained) {
        remaining = null;
        prevBand = band;
        nestedActors.push(raw);
        chainHeadReached.push(Math.max(raw.size, 1));
        continue;
      }
      if (remaining == null || prevBand !== band) {
        remaining = raw;
        headSize = Math.max(raw.size, 1);
      } else {
        remaining = intersect(remaining, raw);
      }
      prevBand = band;
      nestedActors.push(remaining);
      chainHeadReached.push(headSize);
    }

    const rows: FunnelRow[] = steps.map((step, i) => {
      const band = stepBand(step);
      const raw = actorsWith(step.id);
      const chained = band === 'pre' || band === 'stair';
      const reached = chained ? nestedActors[i].size : raw.size;
      const prevChained = i > 0 && stepBand(steps[i - 1]) === band && chained;
      const prevReached = prevChained ? nestedActors[i - 1].size : 0;
      const nextLinear = linear.find((s) => s.n > step.n);
      const delays: number[] = [];
      if (nextLinear && !step.fork) {
        for (const inner of touches.values()) {
          const a = inner.get(step.id);
          const b = inner.get(nextLinear.id);
          if (a != null && b != null && b >= a) delays.push((b - a) / 36e5);
        }
      }
      const pctOfPrev = prevChained && prevReached > 0 ? Math.round((reached / prevReached) * 1000) / 10 : null;
      const head = chainHeadReached[i] || 1;
      const barPct = chained
        ? Math.round((reached / head) * 1000) / 10
        : cohort > 0
          ? Math.min(100, Math.round((reached / cohort) * 1000) / 10)
          : 0;
      return {
        id: step.id,
        n: step.n,
        phase: step.phase,
        label: step.label,
        fork: step.fork,
        band,
        reached,
        reachedAny: raw.size,
        pctOfCohort: cohort > 0 ? Math.round((reached / cohort) * 1000) / 10 : 0,
        pctOfPrev,
        dropOff: pctOfPrev == null ? null : Math.round((100 - pctOfPrev) * 10) / 10,
        barPct,
        medianHoursToNext: median(delays),
      };
    });

    logger.technical('fetch_breadcrumb_funnel_ok', 'breadcrumbFunnelService', {
      role: params.role,
      cohort,
      steps: rows.length,
    });
    return { role: params.role, cohort, rows };
  } catch (err: any) {
    logger.error('fetch_breadcrumb_funnel', 'breadcrumbFunnelService', { error: String(err?.message || err) });
    throw err;
  }
}
